import React, { forwardRef, useMemo, useCallback, useRef, useEffect, useState } from 'react';
import classNames from 'classnames';
import Validation from './Validation';
import './RichTextInput.scss';

const BODY_MAX_CHARS = 650;
const FULL_TEXT_MAX_CHARS = 1300;

export interface IRichTextInputProps {
	id?: string;
	name?: string;
	className?: string;
	placeholder?: string;
	disabled?: boolean;
	readOnly?: boolean;
	value?: string;
	fieldType?: 'title' | 'description' | 'body';
	postType?: string;
	isTouched?: boolean;
	isValid?: boolean;
	invalidFeedback?: string;
	validFeedback?: string;
	isValidMessage?: boolean;
	isTooltipFeedback?: boolean;
	onChange?: (value: string, metadata?: ContentMetadata) => void;
	onBlur?: () => void;
	onFocus?: () => void;
}

export interface ContentMetadata {
	wordCount: number;
	characterCount: number;
	readingTime: number;
	hasFormatting: boolean;
	containsLinks: boolean;
}

/**
 * Rich Text Input Component
 * React 19 compatible implementation using contentEditable
 * Extends existing form component patterns while adding rich text capabilities
 */
const RichTextInput = forwardRef<HTMLDivElement, IRichTextInputProps>(
	(
		{
			id,
			name,
			className,
			placeholder,
			disabled = false,
			readOnly = false,
			value = '',
			fieldType = 'body',
			postType,
			isTouched = false,
			isValid = false,
			invalidFeedback = '',
			validFeedback = '',
			isValidMessage = false,
			isTooltipFeedback = false,
			onChange,
			onBlur,
			onFocus,
			...props
		},
		ref,
	) => {
		const contentRef = useRef<HTMLDivElement>(null);
		const [content, setContent] = useState(value || '');
		const [showAdvancedTools, setShowAdvancedTools] = useState(false);
		const [showLimitAlert, setShowLimitAlert] = useState(false);
		const isUpdatingFromProps = useRef(false);

		const bodyMaxChars = postType === 'FULL_TEXT_POST' ? FULL_TEXT_MAX_CHARS : BODY_MAX_CHARS;

		// Save and restore cursor position
		const saveCursorPosition = useCallback(() => {
			if (!contentRef.current) return null;
			
			const selection = window.getSelection();
			if (!selection || selection.rangeCount === 0) return null;
			
			const range = selection.getRangeAt(0);
			return {
				startContainer: range.startContainer,
				startOffset: range.startOffset,
				endContainer: range.endContainer,
				endOffset: range.endOffset
			};
		}, []);

		const restoreCursorPosition = useCallback((position: any) => {
			if (!position || !contentRef.current) return;
			
			try {
				const selection = window.getSelection();
				if (!selection) return;
				
				const range = document.createRange();
				range.setStart(position.startContainer, position.startOffset);
				range.setEnd(position.endContainer, position.endOffset);
				
				selection.removeAllRanges();
				selection.addRange(range);
			} catch (e) {
				// Ignore cursor position errors
				console.debug('Could not restore cursor position:', e);
			}
		}, []);

		// Update content when value prop changes (but not during user input)
		useEffect(() => {
			if (!isUpdatingFromProps.current && value !== content && contentRef.current) {
				isUpdatingFromProps.current = true;
				const cursorPos = saveCursorPosition();
				
				const newValue = value || '';
				setContent(newValue);
				if (contentRef.current.innerHTML !== newValue) {
					contentRef.current.innerHTML = newValue;
				}
				
				// Restore cursor position after a brief delay
				setTimeout(() => {
					restoreCursorPosition(cursorPos);
					isUpdatingFromProps.current = false;
				}, 0);
			}
		}, [value, content, saveCursorPosition, restoreCursorPosition]);

		// Initialize content on mount and when value changes
		useEffect(() => {
			if (contentRef.current) {
				// Always update innerHTML when value changes
				const newValue = value || '';
				if (contentRef.current.innerHTML !== newValue) {
					contentRef.current.innerHTML = newValue;
				}
				setContent(newValue);
			}
		}, [value]);

		// Show advanced tools by default on larger screens for body field
		useEffect(() => {
			const checkScreenSize = () => {
				if (fieldType === 'body') {
					setShowAdvancedTools(window.innerWidth >= 992); // lg breakpoint
				}
			};

			checkScreenSize();
			window.addEventListener('resize', checkScreenSize);
			return () => window.removeEventListener('resize', checkScreenSize);
		}, [fieldType]);

		/**
		 * Get toolbar buttons based on field type
		 */
		const getToolbarButtons = useMemo(() => {
			const basicFormats = ['bold', 'italic', 'underline', 'strikeThrough'];
			const textAlign = ['justifyLeft', 'justifyCenter', 'justifyRight'];
			const lists = ['insertOrderedList', 'insertUnorderedList'];
			const indent = ['indent', 'outdent'];
			const scripts = ['subscript', 'superscript'];
			const colors = ['foreColor', 'hiliteColor'];
			const fonts = ['fontName', 'fontSize'];
			const links = ['createLink', 'unlink'];

			switch (fieldType) {
				case 'title':
					// Minimal formatting for titles - focus on typography
					return {
						basic: ['bold', 'italic'],
						font: ['fontName', 'fontSize'],
						color: ['foreColor'],
						align: ['justifyLeft', 'justifyCenter', 'justifyRight']
					};
				case 'description':
					// Moderate formatting for descriptions - readable and clean
					return {
						basic: ['bold', 'italic', 'underline'],
						font: ['fontName', 'fontSize'],
						color: ['foreColor', 'hiliteColor'],
						align: ['justifyLeft', 'justifyCenter', 'justifyRight'],
						script: ['subscript', 'superscript']
					};
				case 'body':
				default:
					// Full formatting for body text - complete editing experience
					return {
						basic: basicFormats,
						font: fonts,
						color: colors,
						align: textAlign,
						list: lists,
						indent: indent,
						script: scripts,
						link: links
					};
			}
		}, [fieldType]);

		/**
		 * Calculate content metadata
		 */
		const calculateMetadata = useCallback((htmlContent: string): ContentMetadata => {
			const textContent = htmlContent.replace(/<[^>]*>/g, '');
			const wordCount = textContent.trim() ? textContent.trim().split(/\s+/).length : 0;
			const characterCount = textContent.length;
			const readingTime = Math.max(1, Math.ceil(wordCount / 200)); // Assuming 200 WPM
			const hasFormatting = htmlContent !== textContent;
			const containsLinks = /<a\s+[^>]*href/.test(htmlContent);

			return {
				wordCount,
				characterCount,
				readingTime,
				hasFormatting,
				containsLinks,
			};
		}, []);

		/**
		 * Handle content changes
		 */
		const handleInput = useCallback((event?: React.FormEvent<HTMLDivElement>) => {
			if (contentRef.current && !isUpdatingFromProps.current) {
				const htmlContent = contentRef.current.innerHTML;

				if (htmlContent !== content) {
					if (fieldType === 'body') {
						const textOnly = htmlContent.replace(/<[^>]*>/g, '');
						if (textOnly.length > bodyMaxChars) {
							// Revert to last valid content
							contentRef.current.innerHTML = content;
							setShowLimitAlert(true);
							return;
						}
					}

					setContent(htmlContent);
					if (onChange) {
						const metadata = calculateMetadata(htmlContent);
						onChange(htmlContent, metadata);
					}
				}
			}
		}, [onChange, calculateMetadata, content, fieldType, bodyMaxChars]);

		/**
		 * Handle paste events to maintain formatting
		 */
		const handlePaste = useCallback((event: React.ClipboardEvent<HTMLDivElement>) => {
			event.preventDefault();

			const paste = event.clipboardData?.getData('text/plain') || '';
			if (!paste) return;

			if (fieldType === 'body') {
				const currentText = (contentRef.current?.innerHTML || '').replace(/<[^>]*>/g, '');
				const available = bodyMaxChars - currentText.length;
				if (available <= 0) {
					setShowLimitAlert(true);
					return;
				}
				const trimmedPaste = paste.slice(0, available);
				document.execCommand('insertText', false, trimmedPaste);
				if (trimmedPaste.length < paste.length) {
					setShowLimitAlert(true);
				}
			} else {
				document.execCommand('insertText', false, paste);
			}

			handleInput();
		}, [handleInput, fieldType, bodyMaxChars]);

		/**
		 * Execute formatting command
		 */
		const executeCommand = useCallback((command: string, value?: string) => {
			if (!contentRef.current) return;
			
			// Save cursor position before command
			const cursorPos = saveCursorPosition();
			
			// Focus the content div first
			contentRef.current.focus();
			
			// Execute the formatting command
			document.execCommand(command, false, value);
			
			// Update content and trigger onChange
			handleInput();
			
			// Restore cursor position after a brief delay
			setTimeout(() => {
				restoreCursorPosition(cursorPos);
			}, 0);
		}, [handleInput, saveCursorPosition, restoreCursorPosition]);

		/**
		 * Handle color selection
		 */
		const handleColorChange = useCallback((command: string, color: string) => {
			executeCommand(command, color);
		}, [executeCommand]);

		/**
		 * Handle font family selection
		 */
		const handleFontChange = useCallback((font: string) => {
			executeCommand('fontName', font);
		}, [executeCommand]);

		/**
		 * Handle font size change
		 */
		const handleFontSizeChange = useCallback((size: string) => {
			executeCommand('fontSize', size);
		}, [executeCommand]);

		/**
		 * Handle link creation
		 */
		const handleCreateLink = useCallback(() => {
			const url = prompt('Enter URL:');
			if (url) {
				executeCommand('createLink', url);
			}
		}, [executeCommand]);

		/**
		 * Get button title for tooltip
		 */
		const getButtonTitle = useCallback((command: string): string => {
			const titles: Record<string, string> = {
				'bold': 'Bold',
				'italic': 'Italic',
				'underline': 'Underline',
				'strikeThrough': 'Strikethrough',
				'subscript': 'Subscript',
				'superscript': 'Superscript',
				'justifyLeft': 'Align Left',
				'justifyCenter': 'Center',
				'justifyRight': 'Align Right',
				'justifyFull': 'Justify',
				'insertOrderedList': 'Numbered List',
				'insertUnorderedList': 'Bullet List',
				'indent': 'Indent',
				'outdent': 'Outdent',
				'createLink': 'Add Link',
				'unlink': 'Remove Link'
			};
			return titles[command] || command;
		}, []);

		/**
		 * Render toolbar with organized button groups
		 */
		const renderToolbar = useCallback(() => {
			const buttonGroups = getToolbarButtons;
			const components: React.ReactElement[] = [];

			// For body field type, create collapsible sections for better mobile experience
			const isBodyType = fieldType === 'body';

			// Basic formatting buttons
			if (buttonGroups.basic) {
				components.push(
					<div key="basic" className="btn-group btn-group-sm flex-shrink-0" role="group" style={{ marginRight: '4px' }}>
						{buttonGroups.basic.map((command) => (
							<button
								key={command}
								type="button"
								className="btn btn-outline-secondary"
								style={{ minWidth: '32px', padding: '2px 6px' }}
								onClick={() => executeCommand(command)}
								disabled={disabled || readOnly}
								title={getButtonTitle(command)}
							>
								{getButtonIcon(command)}
							</button>
						))}
					</div>
				);
			}

			// Font selection
			if (buttonGroups.font?.includes('fontName')) {
				components.push(
					<div key="font" className="flex-shrink-0" style={{ marginRight: '4px' }}>
						<select
							className="form-select form-select-sm"
							style={{ maxWidth: '100px', minWidth: '80px', fontSize: '12px' }}
							onChange={(e) => handleFontChange(e.target.value)}
							disabled={disabled || readOnly}
							title="Font Family"
						>
							<option value="">Font</option>
							<option value="Arial">Arial</option>
							<option value="Times New Roman">Times</option>
							<option value="Courier New">Courier</option>
							<option value="Helvetica">Helvetica</option>
							<option value="Georgia">Georgia</option>
							<option value="Verdana">Verdana</option>
						</select>
					</div>
				);
			}

			// Font size
			if (buttonGroups.font?.includes('fontSize')) {
				components.push(
					<div key="fontSize" className="flex-shrink-0" style={{ marginRight: '4px' }}>
						<select
							className="form-select form-select-sm"
							style={{ maxWidth: '70px', minWidth: '60px', fontSize: '12px' }}
							onChange={(e) => handleFontSizeChange(e.target.value)}
							disabled={disabled || readOnly}
							title="Font Size"
						>
							<option value="">Size</option>
							<option value="1">XS</option>
							<option value="3">S</option>
							<option value="5">M</option>
							<option value="7">L</option>
						</select>
					</div>
				);
			}

			// Color controls
			if (buttonGroups.color) {
				components.push(
					<div key="color" className="d-flex flex-shrink-0" style={{ marginRight: '4px', gap: '2px' }}>
						{buttonGroups.color.includes('foreColor') && (
							<input
								type="color"
								className="form-control form-control-color form-control-sm"
								style={{ width: '32px', height: '28px', padding: '0', border: 'none' }}
								title="Text Color"
								onChange={(e) => handleColorChange('foreColor', e.target.value)}
								disabled={disabled || readOnly}
							/>
						)}
						{buttonGroups.color.includes('hiliteColor') && (
							<input
								type="color"
								className="form-control form-control-color form-control-sm"
								style={{ width: '32px', height: '28px', padding: '0', border: 'none' }}
								title="Background Color"
								onChange={(e) => handleColorChange('hiliteColor', e.target.value)}
								disabled={disabled || readOnly}
							/>
						)}
					</div>
				);
			}

			// Text alignment
			if (buttonGroups.align) {
				components.push(
					<div key="align" className="btn-group btn-group-sm flex-shrink-0" role="group" style={{ marginRight: '4px' }}>
						{buttonGroups.align.map((command) => (
							<button
								key={command}
								type="button"
								className="btn btn-outline-secondary"
								style={{ minWidth: '28px', padding: '2px 4px' }}
								onClick={() => executeCommand(command)}
								disabled={disabled || readOnly}
								title={getButtonTitle(command)}
							>
								{getButtonIcon(command)}
							</button>
						))}
					</div>
				);
			}

			// Lists
			if (buttonGroups.list) {
				components.push(
					<div key="list" className="btn-group btn-group-sm flex-shrink-0" role="group" style={{ marginRight: '4px' }}>
						{buttonGroups.list.map((command) => (
							<button
								key={command}
								type="button"
								className="btn btn-outline-secondary"
								style={{ minWidth: '28px', padding: '2px 4px' }}
								onClick={() => executeCommand(command)}
								disabled={disabled || readOnly}
								title={getButtonTitle(command)}
							>
								{getButtonIcon(command)}
							</button>
						))}
					</div>
				);
			}

			// Indent controls
			if (buttonGroups.indent) {
				components.push(
					<div key="indent" className="btn-group btn-group-sm flex-shrink-0" role="group" style={{ marginRight: '4px' }}>
						{buttonGroups.indent.map((command) => (
							<button
								key={command}
								type="button"
								className="btn btn-outline-secondary"
								style={{ minWidth: '28px', padding: '2px 4px' }}
								onClick={() => executeCommand(command)}
								disabled={disabled || readOnly}
								title={getButtonTitle(command)}
							>
								{getButtonIcon(command)}
							</button>
						))}
					</div>
				);
			}

			// Script (sub/super)
			if (buttonGroups.script) {
				components.push(
					<div key="script" className="btn-group btn-group-sm flex-shrink-0" role="group" style={{ marginRight: '4px' }}>
						{buttonGroups.script.map((command) => (
							<button
								key={command}
								type="button"
								className="btn btn-outline-secondary"
								style={{ minWidth: '32px', padding: '2px 4px' }}
								onClick={() => executeCommand(command)}
								disabled={disabled || readOnly}
								title={getButtonTitle(command)}
							>
								{getButtonIcon(command)}
							</button>
						))}
					</div>
				);
			}

			// Link controls
			if (buttonGroups.link) {
				components.push(
					<div key="link" className="btn-group btn-group-sm flex-shrink-0" role="group" style={{ marginRight: '4px' }}>
						<button
							type="button"
							className="btn btn-outline-secondary"
							style={{ minWidth: '32px', padding: '2px 6px' }}
							onClick={handleCreateLink}
							disabled={disabled || readOnly}
							title="Add Link"
						>
							🔗
						</button>
						<button
							type="button"
							className="btn btn-outline-secondary"
							style={{ minWidth: '32px', padding: '2px 6px' }}
							onClick={() => executeCommand('unlink')}
							disabled={disabled || readOnly}
							title="Remove Link"
						>
							🔗⃠
						</button>
					</div>
				);
			}

			// Wrap components in responsive container
			if (isBodyType && components.length > 6) {
				// For body type with many tools, create collapsible sections on smaller screens
				const primaryTools = components.slice(0, 3); // Basic formatting + font + colors
				const secondaryTools = components.slice(3); // Everything else
				
				return (
					<>
						{/* Primary tools - always visible */}
						<div className="d-flex flex-wrap align-items-center" style={{ gap: '2px' }}>
							{primaryTools}
							<button
								type="button"
								className="btn btn-sm btn-outline-secondary ms-1 d-lg-none"
								onClick={() => setShowAdvancedTools(!showAdvancedTools)}
								title={showAdvancedTools ? "Hide Advanced Tools" : "Show More Tools"}
								style={{ fontSize: '12px', minWidth: '28px', padding: '2px 6px' }}
							>
								{showAdvancedTools ? '↑' : '⋯'}
							</button>
						</div>
						
						{/* Secondary tools - always visible on large screens, collapsible on small screens */}
						<div className={`d-lg-flex ${showAdvancedTools ? 'd-flex' : 'd-none'} flex-wrap align-items-center mt-lg-0 mt-2 pt-lg-0 pt-2 border-lg-0 border-top`} style={{ gap: '2px' }}>
							{secondaryTools}
						</div>
					</>
				);
			}

			return (
				<div className="d-flex flex-wrap align-items-center" style={{ gap: '2px' }}>
					{components}
				</div>
			);
		}, [getToolbarButtons, executeCommand, handleColorChange, handleFontChange, handleFontSizeChange, handleCreateLink, disabled, readOnly, fieldType, showAdvancedTools]);

		/**
		 * Handle toolbar button click
		 */
		const handleToolbarClick = useCallback((command: string) => {
			executeCommand(command);
		}, [executeCommand]);

		const classes = useMemo(
			() =>
				classNames(
					'rich-text-input',
					{
						'is-valid': isValid,
						'is-invalid': !isValid && isTouched && (invalidFeedback || !isValidMessage),
					},
					className,
				),
			[className, isValid, isTouched, invalidFeedback, isValidMessage],
		);

		const toolbarClasses = useMemo(
			() =>
				classNames(
					'rich-text-toolbar p-2 border-bottom bg-light overflow-hidden',
					`rich-text-toolbar--${fieldType}`,
					{
						'rich-text-toolbar--compact': fieldType === 'title' || fieldType === 'description',
						'rich-text-toolbar--full': fieldType === 'body'
					}
				),
			[fieldType],
		);

		const editorClasses = useMemo(
			() =>
				classNames(
					'rich-text-editor',
					{
						'rich-text-editor--disabled': disabled,
						'rich-text-editor--readonly': readOnly,
					},
				),
			[disabled, readOnly],
		);

		return (
			<div className={classes} {...props}>
				<style>{`
					.rich-text-toolbar {
						max-width: 100%;
						overflow-x: auto;
						scrollbar-width: thin;
						position: relative;
					}
					.rich-text-toolbar::-webkit-scrollbar {
						height: 4px;
					}
					.rich-text-toolbar::-webkit-scrollbar-thumb {
						background-color: #ccc;
						border-radius: 2px;
					}
					.rich-text-toolbar--compact {
						min-height: 42px;
					}
					.rich-text-toolbar--full {
						min-height: 46px;
					}
					.rich-text-toolbar .btn-group {
						white-space: nowrap;
					}
					.rich-text-toolbar .btn {
						display: inline-flex;
						align-items: center;
						justify-content: center;
					}
					@media (max-width: 991.98px) {
						.rich-text-toolbar .form-select {
							max-width: 70px !important;
						}
						.rich-text-toolbar .btn-group {
							margin-bottom: 2px;
						}
					}
					@media (max-width: 576px) {
						.rich-text-toolbar .btn {
							padding: 1px 4px !important;
							font-size: 11px !important;
						}
						.rich-text-toolbar .form-select {
							font-size: 11px !important;
							max-width: 60px !important;
						}
					}
				`}</style>
				{/* Toolbar */}
				<div className={toolbarClasses}>
					{renderToolbar()}
				</div>

				{/* Editor */}
				<div
					ref={contentRef}
					className={editorClasses}
					contentEditable={!disabled && !readOnly}
					onInput={handleInput}
					onPaste={handlePaste}
					onFocus={onFocus}
					onBlur={onBlur}
					data-placeholder={placeholder}
					suppressContentEditableWarning={true}
				/>

				{/* Validation */}
				<Validation
					isTouched={isTouched}
					invalidFeedback={invalidFeedback}
					validFeedback={validFeedback}
					isTooltip={isTooltipFeedback}
				/>

				{/* Content Metadata Bar */}
				<ContentMetadataBar content={content} fieldType={fieldType} maxChars={fieldType === 'body' ? bodyMaxChars : undefined} />

				{/* Character limit alert dialog */}
				{showLimitAlert && (
					<div
						style={{
							position: 'fixed',
							inset: 0,
							backgroundColor: 'rgba(0,0,0,0.45)',
							zIndex: 9999,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
						onClick={() => setShowLimitAlert(false)}
					>
						<div
							style={{
								background: '#fff',
								borderRadius: '10px',
								padding: '28px 32px',
								maxWidth: '380px',
								width: '90%',
								boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
								textAlign: 'center',
							}}
							onClick={(e) => e.stopPropagation()}
						>
							<div style={{ fontSize: '2rem', marginBottom: '10px' }}>⚠️</div>
							<h5 style={{ color: '#dc3545', marginBottom: '10px', fontWeight: 700 }}>
								Character Limit Exceeded
							</h5>
							<p style={{ color: '#555', marginBottom: '20px', fontSize: '0.95rem' }}>
								Body content cannot exceed <strong>{bodyMaxChars} characters</strong>.
								Please shorten your text.
							</p>
							<button
								className="btn btn-danger px-4"
								onClick={() => setShowLimitAlert(false)}
							>
								OK
							</button>
						</div>
					</div>
				)}
			</div>
		);
	},
);

/**
 * Get button icon for toolbar commands
 */
const getButtonIcon = (command: string): React.ReactNode => {
	const icons: Record<string, React.ReactNode> = {
		bold: <strong>B</strong>,
		italic: <em>I</em>,
		underline: <u>U</u>,
		strikeThrough: <s>S</s>,
		subscript: <>X<sub>2</sub></>,
		superscript: <>X<sup>2</sup></>,
		justifyLeft: '⬅',
		justifyCenter: '↔',
		justifyRight: '➡',
		justifyFull: '⬌',
		insertOrderedList: '1.',
		insertUnorderedList: '•',
		indent: '→',
		outdent: '←',
		createLink: '🔗',
		unlink: '🔗⃠',
	};
	return icons[command] || command;
};

/**
 * Content Metadata Bar Component
 */
const ContentMetadataBar: React.FC<{ content: string; fieldType: string; maxChars?: number }> = ({ content, fieldType, maxChars }) => {
	const metadata = useMemo(() => {
		const textContent = content.replace(/<[^>]*>/g, '');
		const wordCount = textContent.trim() ? textContent.trim().split(/\s+/).length : 0;
		const characterCount = textContent.length;
		return { wordCount, characterCount };
	}, [content]);

	if (fieldType === 'title' && metadata.wordCount === 0) return null;

	const nearLimit = maxChars && metadata.characterCount >= maxChars * 0.85;
	const atLimit = maxChars && metadata.characterCount >= maxChars;
	const charColor = atLimit ? '#dc3545' : nearLimit ? '#fd7e14' : undefined;

	return (
		<div className="rich-text-metadata">
			<small className="text-muted">
				{metadata.wordCount} words
				{maxChars ? (
					<span style={{ marginLeft: '8px', color: charColor, fontWeight: nearLimit ? 600 : undefined }}>
						· {metadata.characterCount} / {maxChars} characters
					</span>
				) : (
					`, ${metadata.characterCount} characters`
				)}
			</small>
		</div>
	);
};

RichTextInput.displayName = 'RichTextInput';

export default RichTextInput;